import { useEffect, useState } from "react";
import { useCreate, useList, useTranslate, useUpdate } from "@refinedev/core";
import { ActionBar, Button, FieldShell, FormCard, SectionHeader } from "@fawkes/ui";

interface WassenSoort {
  id: number;
  naam: string;
  version?: number;
}

// jve-2-4 / jve-2-6 parity pass: wassen-soort detail (docasoft) → mirrors the Jmix
// `wassen-soort-detail-view` formLayout: static title + a labelled `naam` field (1 col, 2 cols ≥40em).
// The Jmix form also shows a `version` textField, but that is the JPA optimistic-lock counter the UI
// intentionally hides (infra) — `naam` is the only editable business field.
//
// wassen-soort-write: the detail is now an EDITABLE form, matching the Jmix detail (Annuleren · Opslaan,
// and a "+ Nieuw" create path). `id === "new"` opens an empty create form (POST); otherwise the row is
// resolved from the (already-cached) list and edited in place (PUT carrying the optimistic-lock version).
// A stale version → a "gewijzigd door iemand anders" notice (the BasisTile/ProductDetail idiom).
// ROLE_PRODUCT_EDIT is enforced server-side (POST/PUT → 403); the affordance stays enabled.
export function WassenSoortDetail({ id, onBack }: { id: string; onBack: () => void }) {
  const t = useTranslate();
  const isNew = id === "new";

  // Edit mode resolves the row from the list (the module exposes no GET /{id}); skip the fetch when creating.
  const { data, isLoading, isError } = useList<WassenSoort>({
    resource: "wassen-soorten",
    queryOptions: { enabled: !isNew },
  });
  const row = isNew ? undefined : data?.data.find((r) => String(r.id) === id);

  const [naam, setNaam] = useState("");
  const [notice, setNotice] = useState<"required" | "conflict" | "error" | null>(null);

  // Seed the form from the resolved row (once it loads). Create mode starts blank.
  useEffect(() => {
    if (row) setNaam(row.naam);
  }, [row]);

  const { mutate: create, isLoading: creating } = useCreate();
  const { mutate: update, isLoading: updating } = useUpdate();
  const saving = creating || updating;

  const save = () => {
    const trimmed = naam.trim();
    if (!trimmed) {
      setNotice("required");
      return;
    }
    setNotice(null);
    if (isNew) {
      create(
        { resource: "wassen-soorten", values: { naam: trimmed }, successNotification: false, errorNotification: false },
        { onSuccess: () => onBack(), onError: () => setNotice("error") },
      );
    } else if (row) {
      update(
        {
          resource: "wassen-soorten",
          id: row.id,
          values: { naam: trimmed, version: row.version },
          successNotification: false,
          errorNotification: false,
        },
        {
          onSuccess: () => onBack(),
          onError: (err) =>
            setNotice((err as { statusCode?: number }).statusCode === 409 ? "conflict" : "error"),
        },
      );
    }
  };

  if (!isNew && isLoading) {
    return <p className="fawkes-muted">{t("common.loading")}</p>;
  }
  if (!isNew && (isError || !row)) {
    return <p className="fawkes-error">{t("wassenSoortDetailView.loadFailed")}</p>;
  }

  return (
    <FormCard>
      <SectionHeader>
        {isNew ? t("wassenSoortDetailView.new.title") : t("wassenSoortDetailView.title")}
      </SectionHeader>
      <div className="fawkes-form-grid">
        <FieldShell label={t("wassenSoortDetailView.naam")} required>
          <input
            className="fui-field__input"
            value={naam}
            maxLength={50}
            onChange={(e) => setNaam(e.target.value)}
          />
        </FieldShell>
      </div>
      {notice === "required" && <p className="fawkes-error">{t("wassenSoortDetailView.naam.required")}</p>}
      {notice === "conflict" && <p className="fawkes-error">{t("wassenSoortDetailView.save.conflict")}</p>}
      {notice === "error" && <p className="fawkes-error">{t("wassenSoortDetailView.save.error")}</p>}
      <ActionBar>
        <Button variant="secondary" onClick={onBack} disabled={saving}>
          {t("common.cancel")}
        </Button>
        <Button variant="primary" onClick={save} disabled={saving}>
          {t("common.save")}
        </Button>
      </ActionBar>
    </FormCard>
  );
}
